import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  tap,
  finalize,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { IAuthorResponse } from 'src/app/core/models/i-author-response';
import { IUserSettings } from 'src/app/core/models/i-user-settings';
import { AuthorService } from 'src/app/shared/services/author.service';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { FORM_FIELDS } from './constants/form-fields';
import { FORM_PATHS } from './constants/form-paths';
import { AlertService } from 'src/app/shared/services/alert.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ISaveUserSettingsResponse } from 'src/app/core/models/i-save-user-settings-response';
import { SaveUserSettingsErrorCode } from 'src/app/content-list-management/shared/enums/SaveUserSettingsErrorCode';
import { ViewportScroller } from '@angular/common';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { Author, IAuthor } from 'src/app/core/models/i-author';
import { WithLanguagesFormSection } from 'src/app/shared/mixins/with-languages-form-section';
import { WithWebsitesFormSection } from 'src/app/shared/mixins/with-websites-form-section';
import { IAutoCompleteData } from 'src/app/shared-form-sections/interfaces/i-auto-complete-data';

class UserSettingsFormComponentBase {
  FORM_PATHS = FORM_PATHS;
  destroy$!: Subject<boolean>;
  form!: FormGroup;

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public formBuilder: FormBuilder,
  ) {}
}

const UserSettingsFormComponentMixinBase = WithWebsitesFormSection(
  WithLanguagesFormSection(WithProcessing(UserSettingsFormComponentBase)),
);

@Component({
  selector: 'app-user-settings-form',
  templateUrl: './user-settings-form.component.html',
  styleUrls: ['./user-settings-form.component.scss'],
})
export class UserSettingsFormComponent
  extends UserSettingsFormComponentMixinBase
  implements OnInit, OnDestroy
{
  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  userSettings$!: Observable<IUserSettings>;
  prevUserSettings!: IUserSettings;
  destroy$ = new Subject<boolean>();

  searchList: IAutoCompleteData[];
  authorList: IAuthor[];

  isSearchingAuthors = false;
  isSaving = false;

  form = this.formBuilder.group({
    [this.FORM_FIELDS.EMAIL]: ['', Validators.email],
    [this.FORM_FIELDS.AUTHORS]: this.formBuilder.group({
      [this.FORM_FIELDS.SEARCHED_AUTHOR]: [null],
      [this.FORM_FIELDS.SELECTED_AUTHORS]: this.formBuilder.array([]),
    }),
    [this.FORM_FIELDS.WEBSITES]: this.formBuilder.group({}),
    [this.FORM_FIELDS.LANGUAGE]: [null],
  });

  ERROR_RESPONSE_CODES_HANDLERS = {
    [SaveUserSettingsErrorCode.None]: () => {},
    [SaveUserSettingsErrorCode.StaleRecord]: () => {
      this.dialogService.openConcurrencyClashDialog({
        message:
          'Your user settings have been updated since you last loaded this page. Reload them to make changes.',
      });
    },
    [SaveUserSettingsErrorCode.UnknownError]: () => {
      this.dialogService.openErrorDialog({
        message:
          'Your user settings could not be saved. Please try again. If the problem persists, please contact your system administrator.',
      });
    },
  };

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public formBuilder: FormBuilder,
    private authorService: AuthorService,
    private userSettingsService: UserSettingsService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private readonly viewport: ViewportScroller,
  ) {
    super(languageService, deliveryTargetsService, formBuilder);
  }

  ngOnInit(): void {
    this._initCurrentSettings();
    this._initLanguages(this.getCurrentSelectedLanguage);
    this._initAuthors();
    this._initDeliveryTargets(this.getCurrentWebsites);
    this._initFormSubs();
  }

  handleAuthorSelected($event) {
    let selectedAuthor = this.authorList.filter((x) => x.id === $event.id);
    this.onAuthorSelected(selectedAuthor[0]);
  }

  onAuthorSelected(author: IAuthor): void {
    const selectedAuthors = this.form.get(
      this.FORM_PATHS.SELECTED_AUTHORS(),
    ) as UntypedFormArray;

    if (selectedAuthors.value.some((sa) => sa.id === author.id)) {
      this.alertService.warning(
        `You've already added the author ${author.name}, see below.`,
      );
      this._resetSearchedAuthorState();
      return;
    }

    this._addSelectedAuthor(author);

    selectedAuthors.markAsDirty();
    this._updateSelectedAuthorsPristineStatus(selectedAuthors);
    this._resetSearchedAuthorState();
  }

  onRemoveAuthor(author: IAuthor): void {
    const selectedAuthors = this.form.get(
      this.FORM_PATHS.SELECTED_AUTHORS(),
    ) as UntypedFormArray;

    selectedAuthors.removeAt(
      selectedAuthors.value.findIndex((sa) => sa.id === author.id),
    );

    selectedAuthors.markAsDirty();
    this._updateSelectedAuthorsPristineStatus(selectedAuthors);
  }

  onSaveSettings(): void {
    this.isSaving = true;
    const updatedUserSettings = {
      ...this.prevUserSettings,
      defaultDeliveryTargetCodes: this._buildDeliveryTargetCodes(),
      defaultArticleAuthors: this._buildAuthors(),
      defaultLanguageCode: this._buildLanguage(),
      emailAddress: this.form.get(this.FORM_PATHS.EMAIL())?.value,
    };

    this.userSettingsService
      .update$(updatedUserSettings)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (response: ISaveUserSettingsResponse) => {
          if (response.success) {
            this.viewport.scrollToPosition([0, 0]);
            this.alertService.success('Settings saved');
            this.prevUserSettings = { ...updatedUserSettings };
            this.form.reset(this.form.value);
            return;
          }

          // TODO ISSUE: Add dialogs with handling logic from the parent component
          this.ERROR_RESPONSE_CODES_HANDLERS[response.errorCode]();
        },
        error: () => {
          this.ERROR_RESPONSE_CODES_HANDLERS[
            SaveUserSettingsErrorCode.UnknownError
          ]();
        },
      });
  }

  private _initCurrentSettings(): void {
    this.userSettings$ = this.process$(
      this.userSettingsService.getForCurrentUser$(),
    ).pipe(
      tap((userSettings: IUserSettings) => {
        this.prevUserSettings = userSettings ?? {};
        this._initFormData();
      }),
    );
  }

  private _initFormData(): void {
    this.form
      .get(this.FORM_PATHS.EMAIL())
      .setValue(this.prevUserSettings.emailAddress);
    this.prevUserSettings.defaultArticleAuthors?.forEach((daa: Author) =>
      this._addSelectedAuthor(daa),
    );
  }

  onSearch($event) {
    this.authorService
      .getFilteredBySearchText$($event)
      .pipe(map((authorsResponse: IAuthorResponse) => authorsResponse.results))
      .subscribe({ next: () => (this.isSearchingAuthors = false) });
  }

  private _initAuthors(): void {
    this.form
      .get(this.FORM_PATHS.SEARCHED_AUTHOR())
      .valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.isSearchingAuthors = true;
        }),
        switchMap((searchedValue) => {
          const getDataObs$ = searchedValue
            ? this.authorService
                .getFilteredBySearchText$(searchedValue)
                .pipe(
                  map(
                    (authorsResponse: IAuthorResponse) =>
                      authorsResponse.results,
                  ),
                )
            : of(null);

          return getDataObs$.pipe(
            finalize(() => {
              this.isSearchingAuthors = false;
            }),
          );
        }),
      )
      .subscribe({
        next: (x) => {
          if (x) {
            this.authorList = x;
            this.searchList = x.map((p) => {
              return { displayName: p.name, id: p.id };
            });
          }
        },
      });
  }

  private _addSelectedAuthor(author: IAuthor): void {
    (
      this.form.get(this.FORM_PATHS.SELECTED_AUTHORS()) as UntypedFormArray
    ).push(this.formBuilder.control(author));
  }

  private _buildAuthors(): IAuthor[] {
    return (
      this.form.get(this.FORM_PATHS.SELECTED_AUTHORS()) as UntypedFormArray
    ).value.map((sa: IAuthor) => sa);
  }

  private _initFormSubs(): void {
    this._initEmailValueChanges();
    this._initWebsitesValueChanges(this.getCurrentWebsites);
    this._initLanguageValueChanges(this.getCurrentSelectedLanguage);
  }

  private _initEmailValueChanges(): void {
    this.form
      .get(this.FORM_PATHS.EMAIL())
      .valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(500),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (newValue) => {
          if (newValue === this.prevUserSettings.emailAddress) {
            this.form.get(this.FORM_PATHS.EMAIL()).markAsPristine();
          }
        },
      });
  }

  private _resetSearchedAuthorState(): void {
    this.form.get(this.FORM_PATHS.SEARCHED_AUTHOR()).setValue('');
    this.form.get(this.FORM_PATHS.SEARCHED_AUTHOR()).markAsPristine();
  }

  private _updateSelectedAuthorsPristineStatus(
    selectedAuthors: UntypedFormArray,
  ): void {
    const hasDataAndIsBackToInitialState =
      this.prevUserSettings?.defaultArticleAuthors?.every((daa) =>
        selectedAuthors.value.some((author) => author.id === daa.id),
      ) &&
      this.prevUserSettings?.defaultArticleAuthors?.length ===
        selectedAuthors.value.length;

    const isEmptyAndIsBackToInitialState =
      !this.prevUserSettings?.defaultArticleAuthors?.length &&
      !selectedAuthors.value.length;

    if (hasDataAndIsBackToInitialState || isEmptyAndIsBackToInitialState) {
      selectedAuthors.markAsPristine();
    }
  }

  getCurrentSelectedLanguage = (): string | null => {
    return this.prevUserSettings.defaultLanguageCode;
  };

  getCurrentWebsites = (): string[] | null => {
    return this.prevUserSettings?.defaultDeliveryTargetCodes;
  };

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
